import { useState } from 'react';
import { useMutation } from 'react-apollo';
import { useSelector } from 'react-redux';

import { getAlbumFolderDiscoveryOnboardingItems } from '../../../../../../util/discover-album-folder';
import { checkout as checkoutMutation } from '../../../../../../graphql/mutations/checkout';
import {
  albumsJoinByCode as albumsJoinByCodeMutation,
  joinByCode as joinByCodeMutation,
} from '../../../../../../graphql/mutations/albums';
import { sentryCaptureMessage } from '../../../../../../util/sentry';
import { albumFolderJoin as albumFolderJoinMutation } from '../../../../../../graphql/mutations/album-folder';
import { AccountForSearchSubscriptionFeaturesQuery } from '../../../../../pro/services/searchSubscriptionFeature/hooks/graphql/queries';
import { formatPriceForDisplay } from '../../../../../../util/format-money';
import { PURCHASE_TYPE } from '../../../../../../components/global/ThankYouView/constants';
import {
  isEligibleForShutterbugFreeTrial,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
} from '../helpers';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import { THANK_YOU_MODAL } from '../../../../../../components/global/ThankYouView';
import { gtm } from '../../../../../../util/analytics';
import usePinterestTracking from '../../../../../../services/pinterest/hooks/usePinterestTracking';
import {
  PINTEREST_CATEGORIES,
  PINTEREST_EVENTS,
} from '../../../../../../services/pinterest/constants';

/**
 * Photo search checkout hook.
 *
 * @param {Object} options The options.
 * @param {Object} options.account The Account object.
 * @param {Function} [options.promoCode] A promo code to be applied on checkout.
 * @param {Object} [options.selectedSearchItem] The selected search item.
 * @param {Array<Object>} [options.searchSubscriptionItems] The available SearchSubscriptionItems.
 * @param {Function} [options.onCheckoutSuccess] Method running on checkout success.
 * @param {Function} [options.onCheckoutError] Method running on checkout error.
 * @returns {{onCheckout: Function, submitting: boolean, checkoutError: string, success: boolean}}
 */
const usePhotoSearchCheckout = ({
  account,
  promoCode,
  selectedSearchItem,
  searchSubscriptionItems = [],
  onCheckoutSuccess,
  onCheckoutError,
}) => {
  const openModal = useOpenModal();
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [success, setSuccess] = useState(false);

  const { onTrackEvent: onTrackPinterestEvent } = usePinterestTracking();

  const identities = useSelector(state => state.discoverAlbumFolder.identities);
  const {
    joinItems,
    albumFolderJoinItem,
  } = getAlbumFolderDiscoveryOnboardingItems(identities);

  const [checkout] = useMutation(checkoutMutation, {
    refetchQueries: [
      {
        query: AccountForSearchSubscriptionFeaturesQuery,
      },
    ],
  });
  const [albumsJoinByCode] = useMutation(albumsJoinByCodeMutation);
  const [joinByCode] = useMutation(joinByCodeMutation);
  const [albumFolderJoin] = useMutation(albumFolderJoinMutation);

  const onCheckout = async ({
    token,
    searchSubscriptionItem,
    stripePaymentMethodId,
    stripeConnectAccountId,
  } = {}) => {
    if (submitting) {
      return;
    }

    const checkoutSearchItem =
      searchSubscriptionItems.find(x => x.id === searchSubscriptionItem?.id) ??
      selectedSearchItem;

    try {
      setSubmitting(true);
      setCheckoutError('');

      // 1. First checkout any available photo search items.
      if (checkoutSearchItem) {
        const { data: { checkout: checkoutResponse } = {} } = await checkout({
          variables: {
            input: {
              account_id: account.id,
              stripe_token: token?.id,
              email_address: token?.email,
              stripe_payment_method_id: stripePaymentMethodId,
              stripe_connect_account_id: stripeConnectAccountId,
              search_subscription_items: checkoutSearchItem?.plan
                ? {
                    search_subscription_item_id: checkoutSearchItem.id,
                  }
                : undefined,
              promo_code: promoCode || token?.promo_code || undefined,
              platform: 'WEB',
            },
          },
        });

        if (checkoutSearchItem?.plan) {
          if (
            checkoutSearchItem.trial_days > 0 &&
            isEligibleForShutterbugFreeTrial(account)
          ) {
            gtm.trackCustomEvent({
              event: 'Plus_Trial_Purchased',
              consumer_subtype: 'CONSUMER',
              user_id: account?.id,
              price: 0,
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Plus_Trial_Purchased',
                content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
                num_items: 1,
                value: 0,
              },
            });
          } else if (
            checkoutSearchItem.billing_period ===
            SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY
          ) {
            gtm.trackCustomEvent({
              event: 'Plus_Monthly_Purchased',
              consumer_subtype: 'CONSUMER',
              user_id: account?.id,
              price: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Plus_Monthly_Purchased',
                content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
                num_items: 1,
                value: formatPriceForDisplay(
                  checkoutResponse?.total_price?.value,
                ),
              },
            });
          } else if (
            checkoutSearchItem.billing_period ===
            SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY
          ) {
            gtm.trackCustomEvent({
              event: 'Plus_Annual_Purchased',
              consumer_subtype: 'CONSUMER',
              user_id: account?.id,
              price: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Plus_Annual_Purchased',
                content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
                num_items: 1,
                value: formatPriceForDisplay(
                  checkoutResponse?.total_price?.value,
                ),
              },
            });
          }

          const purchaseMetadata = {
            subscriptionDetails: {
              searchSubscriptionItem: checkoutSearchItem,
              freeTrialAvailable:
                checkoutSearchItem.trial_days > 0 &&
                isEligibleForShutterbugFreeTrial(account),
            },
            verticalDetails: {
              persona: undefined,
              basename: 'consumer',
            },
          };

          openModal(THANK_YOU_MODAL, {
            purchaseType: PURCHASE_TYPE.SEARCH_SUBSCRIPTION,
            purchaseMetadata,
          });
        }
      }

      // 2. If the checkout is successful (or skipped), join
      // the identities to their selected albums.
      if (joinItems?.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const joinItem of joinItems) {
          // eslint-disable-next-line no-await-in-loop
          await albumsJoinByCode({
            variables: {
              input: {
                account_id: account.id,
                identities: [joinItem.identity_id],
                join_codes: joinItem.join_codes,
              },
            },
          });
        }
      }

      // 3. If the checkout is successful (or skipped), join
      // the identities to an AlbumFolder (if it exists i.e. if it's JOIN_ALL).
      if (albumFolderJoinItem.identities?.length > 0) {
        if (albumFolderJoinItem.join_code) {
          await joinByCode({
            variables: {
              input: {
                account_id: account.id,
                join_code: albumFolderJoinItem.join_code,
                identities: albumFolderJoinItem.identities,
                album_folder_join: true,
              },
            },
          });
        } else {
          await albumFolderJoin({
            variables: {
              input: {
                identity_ids: albumFolderJoinItem.identities,
                album_folder_id: albumFolderJoinItem.albumFolderId,
              },
            },
          });
        }
      }

      setSubmitting(false);
      setSuccess(true);
      onCheckoutSuccess?.(searchSubscriptionItem?.plan);
    } catch (error) {
      setSubmitting(false);
      setCheckoutError(error);
      onCheckoutError?.(error);
      sentryCaptureMessage(error, { level: 'error' });
    }
  };

  return {
    checkoutError,
    submitting,
    success,
    onCheckout,
  };
};

export default usePhotoSearchCheckout;
